import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Link } from 'react-router-dom';

/**
 * Styled markdown renderer for help articles.
 *
 * Component overrides rather than a typography plugin, so every element sits on
 * the --fd-* tokens and follows the light/dark theme. Internal links are routed
 * through react-router so /help/... navigation doesn't reload the SPA.
 */
const components: Components = {
    h1: ({ children }) => (
        <h1 className="text-2xl lg:text-3xl font-bold font-display text-fg mt-8 mb-3 first:mt-0">
            {children}
        </h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-xl lg:text-2xl font-bold font-display text-fg mt-8 mb-3 first:mt-0">
            {children}
        </h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-[17px] font-semibold text-fg mt-6 mb-2">
            {children}
        </h3>
    ),
    p: ({ children }) => (
        <p className="text-[16.5px] lg:text-[17px] leading-[1.65] text-fg-2 mb-4">{children}</p>
    ),
    ul: ({ children }) => (
        <ul className="mb-4 space-y-2 pl-5 list-disc marker:text-gold">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="mb-4 space-y-2 pl-5 list-decimal marker:text-gold">{children}</ol>
    ),
    li: ({ children }) => (
        <li className="text-[16.5px] lg:text-[17px] leading-[1.65] text-fg-2 pl-1">{children}</li>
    ),
    strong: ({ children }) => (
        <strong className="font-semibold text-fg">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    a: ({ href, children }) => {
        const isInternal = href?.startsWith('/');
        const className = 'text-gold underline underline-offset-2 hover:text-gold-hi transition-colors';
        if (isInternal) {
            return <Link to={href!} className={className}>{children}</Link>;
        }
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
                {children}
            </a>
        );
    },
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-gold pl-4 my-4 text-fg-3 italic">
            {children}
        </blockquote>
    ),
    code: ({ children }) => (
        <code className="font-mono text-[14px] bg-card border border-line rounded px-1.5 py-0.5 text-fg">
            {children}
        </code>
    ),
    hr: () => <hr className="my-8 border-0 border-t border-line" />,
    // Tables scroll inside their own container so a wide table never makes the
    // whole page scroll sideways on a phone.
    table: ({ children }) => (
        <div className="my-5 -mx-4 px-4 overflow-x-auto">
            <table className="w-full min-w-[420px] border-collapse text-[15px]">{children}</table>
        </div>
    ),
    thead: ({ children }) => (
        <thead className="border-b border-line">{children}</thead>
    ),
    th: ({ children }) => (
        <th className="text-left py-2 pr-4 font-semibold text-fg whitespace-nowrap">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="py-2.5 pr-4 align-top text-fg-2 border-b border-line">
            {children}
        </td>
    ),
};

export const MarkdownArticle = ({ markdown }: { markdown: string }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={components}
    >
        {markdown}
    </ReactMarkdown>
);
